import { useSyncExternalStore } from "react"

/** What the workspace shows: the request itself, or its signing for the company. */
export type WorkspaceView = "overview" | "sign"

export type WorkspaceWindowMode = "normal" | "maximized" | "focus"

/** The lead or project a request belongs to, as the list already knows it. */
export interface WorkspaceRecord {
  uid: string | null
  name: string | null
  isProject: boolean
  customerName: string | null
}

/** The one signature request open in the EZ-Sign workspace, shown or minimized. */
export interface WorkspaceSession {
  envelopeId: string
  view: WorkspaceView
  window: WorkspaceWindowMode
  minimized: boolean
  /** Shown on the minimized card before the request has loaded. */
  title?: string
  record?: WorkspaceRecord | null
  /** Required fields done, for the minimized card. Held only while signing is open. */
  fields?: { done: number; total: number } | null
  /**
   * Restored from storage after a reload while minimized: nothing is read from
   * the server until the user brings it back, since opening a document to sign
   * is itself recorded.
   */
  dormant?: boolean
}

export interface WorkspaceSize {
  width: number
  height: number
}

const SESSION_KEY = "esign.workspace"
const SIZE_KEY = "esign.workspace.size"
const listeners = new Set<() => void>()

const read = <T>(key: string): T | null => {
  try {
    const raw = sessionStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

const write = (key: string, value: unknown) => {
  try {
    if (value === null) sessionStorage.removeItem(key)
    else sessionStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Memory still holds it.
  }
}

const restored = (): WorkspaceSession | null => {
  const saved = read<WorkspaceSession>(SESSION_KEY)
  if (!saved?.envelopeId) return null
  return saved.minimized ? { ...saved, dormant: true } : saved
}

let current: WorkspaceSession | null = restored()
let size: WorkspaceSize | null = read<WorkspaceSize>(SIZE_KEY)

const publish = (next: WorkspaceSession | null) => {
  current = next
  write(
    SESSION_KEY,
    next && {
      envelopeId: next.envelopeId,
      view: next.view,
      window: next.window,
      minimized: next.minimized,
      title: next.title,
      record: next.record
    }
  )
  listeners.forEach((listener) => listener())
}

/** What the open workspace has under way, and its question before that is left. */
export interface WorkspaceLeaveGuard {
  unfinished(): boolean
  /** True once the user agrees to leave, false to stay. */
  confirm(): Promise<boolean>
}

let leaveGuard: WorkspaceLeaveGuard | null = null
export const setWorkspaceLeaveGuard = (guard: WorkspaceLeaveGuard | null) => {
  leaveGuard = guard
}

export const workspaceHasUnfinishedWork = () =>
  Boolean(current && leaveGuard?.unfinished())

const mayLeave = () =>
  leaveGuard?.unfinished() ? leaveGuard.confirm() : Promise.resolve(true)

/**
 * Opens a request in the workspace. The request already open is brought back
 * as it was, only moved to `view` when one is asked for; another request
 * replaces it once the user agrees to leave unfinished signing.
 */
export const openWorkspace = async ({
  envelopeId,
  view,
  title,
  record
}: {
  envelopeId: string
  view?: WorkspaceView
  title?: string
  record?: WorkspaceRecord | null
}): Promise<boolean> => {
  if (current?.envelopeId === envelopeId) {
    publish({
      ...current,
      view: view ?? current.view,
      minimized: false,
      dormant: false,
      title: current.title ?? title,
      record: current.record ?? record
    })
    return true
  }
  if (current && !(await mayLeave())) return false
  publish({
    envelopeId,
    view: view ?? "overview",
    window: current?.window === "maximized" ? "maximized" : "normal",
    minimized: false,
    title,
    record
  })
  return true
}

/** Closing asks first when signing is under way; true once it has closed. */
export const closeWorkspace = async ({ force = false } = {}) => {
  if (!current) return true
  if (!force && !(await mayLeave())) return false
  publish(null)
  return true
}

const update = (patch: Partial<WorkspaceSession>) => {
  if (current) publish({ ...current, ...patch })
}

export const minimizeWorkspace = () => update({ minimized: true })

export const restoreWorkspace = () =>
  update({ minimized: false, dormant: false })

export const setWorkspaceView = (view: WorkspaceView) => update({ view })

export const setWorkspaceWindow = (window: WorkspaceWindowMode) =>
  update({ window })

export const setWorkspaceFields = (done: number, total: number) => {
  if (current?.fields?.done !== done || current.fields.total !== total)
    update({ fields: { done, total } })
}

export const setWorkspaceTitle = (title: string) => {
  if (current && current.title !== title) update({ title })
}

/** The session as it is now, for code that runs outside a render. */
export const currentWorkspace = () => current

export const useWorkspaceSession = () =>
  useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => current
  )

/** The size the user last dragged the window to, for the tab's life. */
export const rememberedWorkspaceSize = () => size

export const rememberWorkspaceSize = (next: WorkspaceSize) => {
  size = next
  write(SIZE_KEY, next)
}

// Anything that changed a request on the server from inside the workspace says
// so here, and every list showing requests reads them again.
const changeListeners = new Set<(envelopeId: string) => void>()

export const announceEnvelopeChanged = (envelopeId: string) =>
  changeListeners.forEach((listener) => listener(envelopeId))

export const onEnvelopeChanged = (listener: (envelopeId: string) => void) => {
  changeListeners.add(listener)
  return () => {
    changeListeners.delete(listener)
  }
}

/** For tests: forget everything held in memory. */
export const resetWorkspaceForTests = () => {
  current = restored()
  size = read<WorkspaceSize>(SIZE_KEY)
  leaveGuard = null
  changeListeners.clear()
}
